import React, { useState, useMemo } from 'react'
import { GraniteDataGrid } from '@tecuity/granite-ui/GraniteDataGrid'
import { IconButton } from '@tecuity/granite-ui/IconButton'
import { TextInput } from '@tecuity/granite-ui/TextInput'
import { FilterablePopover } from '@tecuity/granite-ui/FilterablePopover'
import { TabButton } from '@tecuity/granite-ui/TabButton'
import { useRequestData } from '../../../Utilities/hooks'
import CrossHeavy from '@tecuity/sos-icons/react/cross-heavy'
import { ContextButton } from '@tecuity/granite-ui/ContextButton'
import { GET_ALL_GL_ACCOUNT_TYPES } from '../../../requests'

const ReceiptItemDistribution = ({ property = {}, onChange, receiptItemTypeId = 0 }) => {
  //const property = dummyProperty
  const accounts = useRequestData(GET_ALL_GL_ACCOUNT_TYPES)
  const [popoverLocation, setPopoverLocation] = useState({ top: 0, left: 0 })
  const [popupOpen, setPopupOpen] = useState(false)
  const [selectedRow, setSelectedRow] = useState({})

  const openPopup = (e) => {
    if (!document.getElementsByClassName('popoverJSSelector')[0]) {
      return
    }
    const button = e.target.nodeType === 'button' ? e.target : e.target.closest('button')
    const bodyRect = document.body.getBoundingClientRect()
    const elemRect = button.getBoundingClientRect()
    const buttonLocationY = elemRect.top - bodyRect.top
    const buttonLocationX = elemRect.left - bodyRect.left
    const buttonHeight = button.offsetHeight
    const fullHeight = 20 + accounts.length * 24.59 // filterInputHeight + (accounts.length * optionHeight)
    const popoverHeight = fullHeight > 352 ? 352 : fullHeight // max height of 352

    let calculatedPopoverY = buttonLocationY + buttonHeight - window.scrollY
    let calculatedPopoverX = buttonLocationX

    // if the popover would overflow the bottom of the screen, move it up
    if (calculatedPopoverY + popoverHeight > window.innerHeight) {
      calculatedPopoverY = calculatedPopoverY - popoverHeight - buttonHeight
      // console.log('after calculating: ', calculatedPopoverY)
    } else {
      // console.log('not overflowing bottom')
    }

    setPopoverLocation({ top: calculatedPopoverY, left: calculatedPopoverX })
    setPopupOpen(true)
  }

  const rows = useMemo(() => {
    if (!property.v)
      return [
        {
          RowId: -1,
          Name: 'Total Distribution',
          Percentage: 0,
        },
      ]
    const sum = property.v.reduce((sum, account) => sum + (Number(account.Percentage) || 0), 0)
    //console.log('rows', property.v)
    return property.v
      .map((item, index) => {
        return { ...item, RowId: index }
      })
      .concat({
        RowId: -1,
        Name: 'Total Distribution',
        Percentage: sum,
      })
  }, [property.v])

  const columns = [
    {
      field: 'Name',
      flex: 1,
      headerName: 'GL Account',
      disableColumnMenu: true,
      renderCell: (params) => {
        if (params.row.RowId === -1)
          return <p style={{ fontWeight: 'bolder' }}>{params.row.Name}</p>

        const account =
          accounts && accounts.length > 0 && params.row.GeneralLedgerAccountId
            ? accounts.find((account) => account.Id === params.row.GeneralLedgerAccountId)
            : { Name: 'Select Account' }
        const accountName = account ? account.Name : 'Account Not Found'

        return (
          <div>
            <TabButton onClick={openPopup} label={accountName} />
          </div>
        )
      },
    },
    {
      field: 'Percentage',
      flex: 0.4,
      headerName: 'Percentage',
      disableColumnMenu: true,
      renderCell: (params) => {
        return params.row.RowId === -1 ? (
          <p style={{ fontWeight: 'bolder' }}>{`${params.row.Percentage}%`}</p>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <TextInput
              aria-label={`${params.row.Name} percentage`}
              value={params.row.Percentage || ''}
              customCss={{ width: '50px' }}
              onChange={(e) => {
                //console.log(e)
                changePercentage(e.target.value)
              }}
            />
            %
          </div>
        )
      },
    },
    {
      field: 'remove',
      flex: 0.3,
      headerName: 'Action',
      disableColumnMenu: true,
      renderCell: (params) => {
        return params.row.RowId === -1 ? null : (
          <div className="action-button-wrapper">
            <IconButton
              label="Remove"
              icon={<CrossHeavy />}
              onClick={() => {
                removeAccount(params.row.RowId)
              }}
            />
          </div>
        )
      },
    },
  ]

  const changePercentage = (val) => {
    const oldItem = { ...property.v[selectedRow.RowId] }
    onChange(
      property.v.toSpliced(selectedRow.RowId, 1, {
        ...oldItem,
        Percentage: val,
      })
    )
  }

  const selectAccount = (account) => {
    const oldItem = { ...property.v[selectedRow.RowId] }
    onChange(
      property.v.toSpliced(selectedRow.RowId, 1, {
        ...oldItem,
        GeneralLedgerAccountId: account.GeneralLedgerAccountId,
      })
    )
    setPopupOpen(false)
  }

  const addAccount = () => {
    onChange([
      ...(property.v || []),
      {
        ReceiptItemGLDistributionConfigId: 0,
        ReceiptItemTypeId: receiptItemTypeId,
        ReceiptItem: null,
        GeneralLedgerAccountId: 0,
        GeneralLedgerAccount: null,
        Percentage: '',
        Formula: '[AMOUNT] * 1',
      },
    ])
  }

  const removeAccount = (rowId) => {
    onChange(property.v.toSpliced(rowId, 1))
  }

  const filteredAccounts = useMemo(() => {
    const usedAccountIds = []
    ;(property.v || []).forEach((item) => {
      // console.log('filteredAccount', item)
      if (item.GeneralLedgerAccountId) usedAccountIds.push(item.GeneralLedgerAccountId)
    })
    // console.log('usedAccountIds', usedAccountIds)
    return accounts
      ? accounts
          .filter((account) => account.IsActive)
          .filter((account) => !usedAccountIds.includes(account.GeneralLedgerAccountId))
      : []
  }, [accounts, property])

  const addAccountDisabled = useMemo(() => {
    if (filteredAccounts.length === 0) return true
    if (rows.length - 1 === accounts.filter((account) => account.IsActive).length) return true
    return false
  }, [filteredAccounts, rows, accounts])

  return (
    <div style={{ marginBottom: '35px' }}>
      <div className="property-label distribution-header">
        {`Distribution`}
        <ContextButton onClick={addAccount} disabled={addAccountDisabled}>
          Add Account
        </ContextButton>
      </div>
      <div id="popover-wrapper" style={{ display: popupOpen ? 'block' : 'none' }}>
        <FilterablePopover
          onClickOutside={() => setPopupOpen(false)}
          className="popoverJSSelector"
          placeholder="Filter Account"
          customCss={`top: ${popoverLocation.top}px; left: ${popoverLocation.left}px; position: absolute; height: fit-content;`}
          options={filteredAccounts.map((account) => {
            //console.log(account)
            return {
              ...account,
              id: account.GeneralLedgerAccountId,
              value: account.GeneralLedgerAccountId,
              textLabel: `${account.Name}`,
              render: (text) => {
                return (
                  <div
                    key={text}
                    onClick={() => {
                      selectAccount(account)
                    }}
                    style={{ paddingTop: '6px', paddingRight: '4px', fontWeight: 'bold' }}
                  >
                    {text}
                  </div>
                )
              },
            }
          })}
        />
      </div>
      <GraniteDataGrid
        getRowId={(row) => row.RowId}
        columns={columns}
        rows={rows}
        onRowClick={(params) => {
          setSelectedRow(params.row)
        }}
      />
    </div>
  )
}

export default ReceiptItemDistribution

// const dummyProperty = {
//   v: [
//     {
//       ReceiptItemGLDistributionConfigId: 3015,
//       ReceiptItemTypeId: 2044,
//       ReceiptItem: null,
//       GeneralLedgerAccountId: 112,
//       GeneralLedgerAccount: null,
//       Percentage: 100,
//       Formula: '[AMOUNT] * 1',
//     },
//     {
//       ReceiptItemGLDistributionConfigId: 3016,
//       ReceiptItemTypeId: 2045,
//       ReceiptItem: null,
//       GeneralLedgerAccountId: 113,
//       GeneralLedgerAccount: null,
//       Percentage: 0,
//       Formula: '[AMOUNT] * 1',
//     },
//   ],
// }
